import { useEffect, useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Modal for adding or editing an item's full attributes.
// Opened from the Settings → Items list (tap a row to edit, or
// "+ Add item" to create).
//
// Key UX rule for EDITS: when the manager edits a Toast-synced field
// (name, cost, unit), we automatically flip the corresponding
// *_override flag. That way the next nightly Toast sync sees their
// edit and respects it instead of overwriting it with Toast's value.
export default function ItemEditModal({
  open,
  onClose,
  onSave,         // (id, patch) → Promise   (edit mode)
  onCreate,       // (payload) → Promise<row> (create mode)
  item,           // optional — null/undefined = create mode
  suppliers,      // for the supplier picker
}) {
  const isCreateMode = !item
  const [form, setForm] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Reset form state every time the modal opens. In edit mode we
  // hydrate from the item so we can detect which fields changed (for
  // override flags); in create mode we start with sensible defaults.
  useEffect(() => {
    if (!open) return
    if (item) {
      setForm({
        name:           item.name ?? '',
        category:       item.category ?? '',
        unit:           item.unit ?? 'each',
        cost_per_unit:  String(item.cost_per_unit ?? '0'),
        sale_price:     item.sale_price != null ? String(item.sale_price) : '',
        par_qty:        String(item.par_qty ?? '0'),
        current_on_hand: String(item.current_on_hand ?? '0'),
        preferred_supplier_id: item.preferred_supplier_id ?? '',
        // Pack-aware: pack_size > 1 means "1 unit contains pack_size pack_units"
        pack_size:      String(item.pack_size ?? '1'),
        pack_unit:      item.pack_unit ?? '',
        active:         !!item.active,
      })
    } else {
      setForm({
        name: '',
        category: '',
        unit: 'each',
        cost_per_unit: '',
        sale_price: '',
        par_qty: '',
        current_on_hand: '0',
        preferred_supplier_id: '',
        pack_size: '1',
        pack_unit: '',
        active: true,
      })
    }
    setError(null)
    setInvoiceHelperOpen(false)
    setInvoiceHelperFields({ cost_per_case: '', units_per_case: '' })
    setSubmitting(false)
  }, [open, item])

  // Invoice Helper local state — kept separate from main form so its
  // values don't leak into the patch unless the manager taps "Apply".
  //
  // Two fields only on purpose: cost-per-case + units-per-case is what
  // every invoice line shows, and that's all you need to derive
  // cost-per-inner-unit. "Cases bought" was originally here too but
  // it's irrelevant to per-unit math (only matters for inventory)
  // and was misread as "cost of one case" by the first pilot user.
  const [invoiceHelperOpen, setInvoiceHelperOpen] = useState(false)
  const [invoiceHelperFields, setInvoiceHelperFields] = useState({
    cost_per_case: '', units_per_case: '',
  })

  function setHelper(key, value) {
    setInvoiceHelperFields((prev) => ({ ...prev, [key]: value }))
  }

  // Apply the helper math to the main form: cost_per_unit becomes
  // the cost-per-case the manager typed, pack_size becomes the
  // units-per-case, and we auto-fill pack_unit + flip unit→case
  // if those are still at defaults.
  function applyInvoiceHelper() {
    const cpc = Number(invoiceHelperFields.cost_per_case)
    const upc = Number(invoiceHelperFields.units_per_case)
    if (!(cpc > 0) || !(upc > 0)) return
    setForm((prev) => ({
      ...prev,
      cost_per_unit: cpc.toFixed(4),
      pack_size:     String(upc),
      pack_unit:     prev.pack_unit?.trim() || 'each',
      // If the parent unit was still the default "each", flip it to "case"
      // since the manager just told us they buy cases.
      unit:          prev.unit === 'each' ? 'case' : prev.unit,
    }))
    setInvoiceHelperOpen(false)
  }

  if (!open || !form) return null

  const isToastItem = !isCreateMode && !!item.toast_guid

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return

    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }
    if (Number(form.cost_per_unit) < 0) {
      setError('Cost per unit must be ≥ 0.')
      return
    }
    if (!(Number(form.pack_size) > 0)) {
      setError('Pack size must be greater than 0.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      if (isCreateMode) {
        await onCreate({
          name: form.name.trim(),
          category: form.category.trim() || null,
          unit: form.unit,
          cost_per_unit: Number(form.cost_per_unit) || 0,
          sale_price: form.sale_price !== '' ? Number(form.sale_price) : null,
          par_qty: Number(form.par_qty) || 0,
          current_on_hand: Number(form.current_on_hand) || 0,
          preferred_supplier_id: form.preferred_supplier_id || null,
          pack_size: Number(form.pack_size) || 1,
          pack_unit: (Number(form.pack_size) || 1) > 1 ? (form.pack_unit?.trim() || 'each') : null,
        })
      } else {
        // Build the patch. For Toast items, also flip override flags
        // when the manager actually changed those fields.
        const packSize = Number(form.pack_size) || 1
        const patch = {
          name: form.name.trim(),
          category: form.category.trim() || null,
          unit: form.unit,
          cost_per_unit: Number(form.cost_per_unit),
          sale_price: form.sale_price !== '' ? Number(form.sale_price) : null,
          par_qty: Number(form.par_qty),
          current_on_hand: Number(form.current_on_hand),
          preferred_supplier_id: form.preferred_supplier_id || null,
          pack_size: packSize,
          // Clear pack_unit when pack_size collapses to 1, so we don't
          // leave stale labels around.
          pack_unit: packSize > 1 ? (form.pack_unit?.trim() || 'each') : null,
          active: form.active,
        }

        if (isToastItem) {
          if (form.name.trim() !== item.name)              patch.name_override = true
          if (Number(form.cost_per_unit) !== Number(item.cost_per_unit)) patch.cost_override = true
          if (form.unit !== item.unit)                     patch.unit_override = true
        }

        // Manual edits to on_hand also bump the stocktake timestamp —
        // signals to Reorder UI that the count is fresh.
        if (Number(form.current_on_hand) !== Number(item.current_on_hand)) {
          patch.last_stocktake_at = new Date().toISOString()
        }

        await onSave(item.id, patch)
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[ItemEditModal] save failed', e2)
      setError('Could not save. Check your connection and try again.')
    } finally {
      // Always reset — modal stays mounted between opens.
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isCreateMode ? 'Add item' : `Edit ${item.name}`}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {isCreateMode ? 'Add item' : `Edit · ${item.name}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* Name + category */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="item-name">Name</label>
            <input
              id="item-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="item-category">Category</label>
            <input
              id="item-category"
              type="text"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              placeholder="protein / side / dry / produce / dairy"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Unit + cost grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="item-unit">Unit</label>
              <select
                id="item-unit"
                value={form.unit}
                onChange={(e) => set('unit', e.target.value)}
                className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              >
                <option value="lb">lb</option>
                <option value="oz">oz</option>
                <option value="count">count</option>
                <option value="each">each</option>
                <option value="case">case</option>
                <option value="gallon">gallon</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="item-cost">Cost / unit</label>
              <input
                id="item-cost"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.cost_per_unit}
                onChange={(e) => set('cost_per_unit', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
              <p className="mt-1 text-xs text-black/50">
                What you pay per {form.unit} — drives waste $ tracking.
              </p>
              {/* Derived $/inner_unit hint when this is a pack-aware item. */}
              {Number(form.pack_size) > 1 && Number(form.cost_per_unit) > 0 && (
                <p className="mt-1 text-xs font-medium text-emerald-700 tabular-nums">
                  = {formatMoney(Number(form.cost_per_unit) / Number(form.pack_size))}/
                  {form.pack_unit?.trim() || 'each'}
                </p>
              )}
            </div>
          </div>

          {/* Pack section — captures the relationship between the
              purchase unit (e.g. case) and the inner unit (e.g. each).
              Optional: a fresh row defaults to pack_size=1 / pack_unit
              empty, which means "no pack break-down" and behaves
              exactly like the pre-0024 single-layer item model. */}
          <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 space-y-3">
            <div className="flex items-baseline justify-between gap-2">
              <div className="text-[11px] font-medium text-black/60">
                Pack
              </div>
              <button
                type="button"
                onClick={() => setInvoiceHelperOpen((v) => !v)}
                className="rounded-md border border-blue-200 bg-white px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                {invoiceHelperOpen ? 'Hide invoice helper' : 'Use invoice helper'}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="item-pack-size">
                  Pack size
                </label>
                <input
                  id="item-pack-size"
                  type="number"
                  inputMode="decimal"
                  // step="any" + min="0" because real pack sizes range
                  // from 1 (no break-down) to 100+ (a 1/2500 case of
                  // souffle cups). A fixed step like 0.01 forces the
                  // browser to reject whole numbers that don't sit on
                  // the (min + n*step) grid — e.g. 24 with min=0.0001
                  // step=0.01 fails because 24 isn't 0.0001 + n*0.01.
                  step="any"
                  min="0"
                  value={form.pack_size}
                  onChange={(e) => set('pack_size', e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
                <p className="mt-1 text-xs text-black/50">
                  Inner units per {form.unit}. Leave at 1 if no pack break-down.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="item-pack-unit">
                  Inner unit
                </label>
                <input
                  id="item-pack-unit"
                  type="text"
                  value={form.pack_unit}
                  onChange={(e) => set('pack_unit', e.target.value)}
                  placeholder={Number(form.pack_size) > 1 ? 'e.g. each / oz / packet' : '—'}
                  disabled={Number(form.pack_size) <= 1}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30 disabled:bg-black/[0.04] disabled:text-black/40"
                />
                <p className="mt-1 text-xs text-black/50">
                  What's inside the {form.unit}? Used by recipes for per-unit costing.
                </p>
              </div>
            </div>

            {invoiceHelperOpen && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                <div className="text-xs font-medium text-blue-900">
                  Invoice helper
                </div>
                <p className="text-xs text-blue-900/75">
                  Look at one line on your invoice. Type the price of a single
                  case and how many units are inside — I'll do the rest.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-medium text-blue-900/70">
                      Cost per case $
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={invoiceHelperFields.cost_per_case}
                      onChange={(e) => setHelper('cost_per_case', e.target.value)}
                      placeholder="26.99"
                      className="w-full rounded-md border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-blue-900/70">
                      Units / case
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={invoiceHelperFields.units_per_case}
                      onChange={(e) => setHelper('units_per_case', e.target.value)}
                      placeholder="24"
                      className="w-full rounded-md border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                    />
                  </div>
                </div>
                {(() => {
                  const cpc = Number(invoiceHelperFields.cost_per_case)
                  const upc = Number(invoiceHelperFields.units_per_case)
                  if (cpc > 0 && upc > 0) {
                    const perInner = cpc / upc
                    return (
                      <div className="text-xs leading-snug text-blue-900/85 tabular-nums">
                        {formatMoney(cpc)} per case &divide; <span className="font-medium">{upc}</span> units
                        {' '}={' '}
                        <span className="font-medium">{formatMoney(perInner)}</span> per inner unit
                      </div>
                    )
                  }
                  return null
                })()}
                <button
                  type="button"
                  onClick={applyInvoiceHelper}
                  disabled={
                    !(Number(invoiceHelperFields.cost_per_case) > 0 &&
                      Number(invoiceHelperFields.units_per_case) > 0)
                  }
                  className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply to item
                </button>
              </div>
            )}
          </div>

          {/* Menu price (from POS) — sits right under cost so the margin
              relationship is visible at a glance. */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="item-price">
              Menu price (sale price)
            </label>
            <input
              id="item-price"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={form.sale_price}
              onChange={(e) => set('sale_price', e.target.value)}
              placeholder="leave blank if not on the menu"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            {form.sale_price && Number(form.sale_price) > 0
              && form.cost_per_unit && Number(form.cost_per_unit) > 0
              && Number(form.cost_per_unit) <= Number(form.sale_price)
              && (
              <p className="mt-1 text-xs text-emerald-700">
                Margin{' '}
                <span className="font-medium tabular-nums">
                  {((1 - Number(form.cost_per_unit) / Number(form.sale_price)) * 100).toFixed(0)}%
                </span>{' '}
                ({formatMoney(Number(form.sale_price) - Number(form.cost_per_unit))} per {form.unit})
              </p>
            )}
            {(!form.sale_price || Number(form.sale_price) === 0) && (
              <p className="mt-1 text-xs text-black/50">
                Filled in from POS imports. Used for margin calculations once cost is set.
              </p>
            )}
          </div>

          {/* Par + on hand grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="item-par">
                Par ({form.unit})
              </label>
              <input
                id="item-par"
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                value={form.par_qty}
                onChange={(e) => set('par_qty', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
              <p className="mt-1 text-xs text-black/50">
                Target on-hand. Reorder screen suggests bringing inventory back to this.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="item-onhand">
                On hand ({form.unit})
              </label>
              <input
                id="item-onhand"
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                value={form.current_on_hand}
                onChange={(e) => set('current_on_hand', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
              <p className="mt-1 text-xs text-black/50">
                {isCreateMode
                  ? 'Starting count. Edit later to refresh.'
                  : 'Editing here counts as a fresh stocktake.'}
              </p>
            </div>
          </div>

          {/* Preferred supplier */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="item-supplier">
              Preferred supplier
            </label>
            <select
              id="item-supplier"
              value={form.preferred_supplier_id}
              onChange={(e) => set('preferred_supplier_id', e.target.value)}
              className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            >
              <option value="">— none —</option>
              {suppliers.filter((s) => s.active).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Active toggle — edit mode only. New items default to active. */}
          {!isCreateMode && (
            <div className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-black/55">
                  Uncheck to hide this item from Waste / Receive / Reorder.
                  Past logs stay so historical totals remain correct — that's
                  why we don't delete items outright.
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-6 w-6 rounded border-black/30 text-blue-600 focus:ring-blue-600"
              />
            </div>
          )}

          {/* Toast metadata footer (read-only) */}
          {isToastItem && (
            <div className="rounded-lg bg-black/5 px-3 py-2 text-xs text-black/60">
              <div>Toast item · last sync status: {item.sync_status ?? 'n/a'}</div>
              {item.toast_raw_unit && (
                <div className="mt-0.5">Toast raw unit: <code>{item.toast_raw_unit}</code></div>
              )}
              <div className="mt-0.5">
                Edits flip override flags so nightly sync won't overwrite them.
              </div>
            </div>
          )}

          {/* Error moved to sticky header. */}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Saving\u2026'
              : isCreateMode
                ? 'Add item'
                : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}
